import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { connectCaseWs } from "../lib/ws";
import { RiskBadge } from "../components/RiskBadge";
import { CanonicalTimeline } from "../components/case/CanonicalTimeline";
import { SharedClinicsPanel, AppointmentsPanel } from "../components/case/SharedClinicsPanel";
import { ArrivalFlow } from "../components/case/ArrivalFlow";
import { partnerUnitLabel, STATUS_LABEL } from "../lib/constants";
import { CheckCircle2, Circle, Upload, FileText, Send, Loader2, Paperclip, FileDown, Key, Eye, EyeOff, Copy, Wifi, WifiOff, MessageCircle, UserCircle2, Star } from "lucide-react";
import { ReviewModal } from "../components/case/ReviewModal";
import { IndicatedProfessionalsPanel } from "../components/case/IndicatedProfessionalsPanel";
import { trackWhatsApp } from "../lib/beacon";

/* eslint-disable no-unused-vars */
const TIMELINE_STEPS = [
  { key: "solicitacao", label: "Solicitação enviada" },
  { key: "triagem", label: "Triagem concluída" },
  { key: "analise", label: "Em análise" },
  { key: "consultor_designado", label: "Consultor designado" },
  { key: "clinica_selecionada", label: "Unidade Parceira indicada" },
  { key: "reserva_confirmada", label: "Reserva confirmada" },
  { key: "internado", label: "Internação realizada" },
  { key: "nao_internado", label: "Encerrado sem internação" },
  { key: "concluido", label: "Caso concluído" },
];
/* eslint-enable no-unused-vars */

const CaseView = () => {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const [wsLive, setWsLive] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [consultantCard, setConsultantCard] = useState(null);
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [c, d, m] = await Promise.all([
        api.get(`/cases/${caseId}`),
        api.get(`/cases/${caseId}/documents`),
        api.get(`/cases/${caseId}/messages`),
      ]);
      setCaseData(c.data);
      setDocs(d.data);
      setMessages(m.data);
      // Card do consultor com bio + avatar
      if (c.data.consultant_id) {
        try {
          const { data } = await api.get(`/public/consultor/${c.data.consultant_id}`);
          setConsultantCard(data);
        } catch (_) { /* consultor pode não existir mais */ }
      } else { setConsultantCard(null); }
      // Carrega avaliação existente da família (silencioso se ainda não avaliou)
      try {
        const { data } = await api.get(`/cases/${caseId}/review`);
        setMyReview(data?.review || null);
      } catch (_err) { /* família ainda não avaliou */ }
    } catch (err) {
      toast.error("Erro ao carregar caso");
    }
  }, [caseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // WebSocket em tempo real substituindo o polling de 5s.
  useEffect(() => {
    const conn = connectCaseWs(caseId, {
      onOpen: () => setWsLive(true),
      onClose: () => setWsLive(false),
      onEvent: (ev) => {
        if (ev.type === "message" || ev.type === "status") {
          fetchAll();
        }
      },
    });
    return () => conn.close();
  }, [caseId, fetchAll]);

  const doneSteps = new Set((caseData?.timeline || []).map((t) => t.step));

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/cases/${caseId}/documents`, fd);
      toast.success("Documento enviado");
      const { data } = await api.get(`/cases/${caseId}/documents`);
      setDocs(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const sendMsg = async () => {
    if (!draft.trim()) return;
    try {
      const { data } = await api.post(`/cases/${caseId}/messages`, { content: draft.trim() });
      setMessages([...messages, data]);
      setDraft("");
    } catch { toast.error("Erro ao enviar"); }
  };

  if (!caseData) return <div className="p-10 text-slate-500">Carregando caso…</div>;
  const d = caseData.dossier;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">Protocolo {caseData.protocol}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">
            {caseData.patient_name}, {caseData.patient_age}
          </h1>
          <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
            <span>{caseData.city}</span>
            <span>·</span>
            <span>{caseData.substance}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <RiskBadge level={caseData.risk_level} size="lg" />
          <span className="text-xs px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-full text-slate-600">
            {STATUS_LABEL[caseData.status] || caseData.status}
          </span>
          <button onClick={() => setShowReview(true)} data-testid="case-review-btn"
            className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold transition-colors ${myReview ? "border border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "border border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100"}`}>
            <Star className="w-3.5 h-3.5" fill={myReview ? "currentColor" : "none"} />
            {myReview ? `Sua avaliação: ${myReview.stars}★ · editar` : "Avaliar atendimento"}
          </button>
          <button
            data-testid="resend-wa-invite-btn"
            onClick={async () => {
              try {
                const { data } = await api.post(`/cases/${caseId}/resend-wa-invite`);
                if (data.ok) toast.success("Convite WhatsApp reenviado por SMS.");
                else toast.info(data.detail || "Convite não foi reenviado (família já ativa ou Sandbox desligado).");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Erro ao reenviar convite");
              }
            }}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold border border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
            title="Reenvia o SMS pedindo à família para mandar 'join <código>' para o Sandbox WhatsApp da Twilio"
          >
            <Send className="w-3.5 h-3.5" /> Reenviar convite WhatsApp
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Coluna principal - Dossiê */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Dossiê Inteligente RAF</div>
                <h2 className="font-display font-bold text-2xl text-[#0B2B62] mt-1">DIR — Documento do caso</h2>
              </div>
              <div className="flex items-center gap-2">
                {d && (
                  <a
                    data-testid="download-dir-pdf"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/dossier.pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <FileDown className="w-4 h-4" /> PDF
                  </a>
                )}
                <FileText className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
              </div>
            </div>

            {!d ? (
              <p className="text-slate-500 text-sm">A triagem ainda não foi concluída. <Link to={`/triagem/${caseId}`} className="text-[#0B2B62] font-semibold hover:underline">Continuar triagem</Link></p>
            ) : (
              <div className="space-y-6">
                <div className={`p-4 rounded-lg border-l-4 border-risk-${d.risk_level}`} style={{ background: caseData.risk_level ? undefined : "#F8FAFC" }}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Classificação de risco</div>
                  <div className="flex items-center gap-3">
                    <RiskBadge level={d.risk_level} size="md" />
                    <span className="text-sm text-slate-700">{d.risk_justification}</span>
                  </div>
                </div>

                <Section title="Resumo clínico"><p className="text-slate-700 leading-relaxed whitespace-pre-line">{d.clinical_summary}</p></Section>
                <Section title="Perfil de dependência"><p className="text-slate-700">{d.dependency_profile}</p></Section>
                <Section title="Situação familiar"><p className="text-slate-700">{d.family_situation}</p></Section>

                <div className="grid md:grid-cols-3 gap-4">
                  <ListSection title="Condições clínicas" items={d.reported_conditions} />
                  <ListSection title="Necessidades especiais" items={d.special_needs} />
                  <ListSection title="Restrições" items={d.restrictions} />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Modalidade recomendada</div>
                    <div className="mt-1 font-display font-semibold text-lg capitalize text-[#0B2B62]">{d.recommended_modality}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Gerado por IA</div>
                    <div className="mt-1 text-sm text-slate-600">GPT-5.2 · Protocolo RAF Direct</div>
                  </div>
                </div>

                <Section title="Ações recomendadas ao consultor">
                  <ul className="space-y-2">
                    {(d.recommended_actions || []).map((a, i) => (
                      <li key={`${a}-${i}`} className="flex items-start gap-2 text-slate-700 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0B2B62] mt-2 shrink-0" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </Section>
              </div>
            )}
          </section>

          {/* Documentos */}
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl text-[#0B2B62]">Central de Documentos</h2>              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-md text-sm text-slate-700 transition-colors">
                <Upload className="w-4 h-4" />
                {uploading ? "Enviando..." : "Enviar arquivo"}
                <input ref={fileRef} data-testid="doc-upload" type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
              </label>
            </div>
            {docs.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum documento enviado ainda. Envie RG, exames, receitas, laudos ou fotos.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {docs.map((doc) => (
                  <li key={doc.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-800 truncate">{doc.original_filename}</div>
                        <div className="text-xs text-slate-500">{doc.uploaded_by_name} · {new Date(doc.created_at).toLocaleString("pt-BR")}</div>
                      </div>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`}
                       target="_blank" rel="noreferrer" className="text-sm text-[#0B2B62] hover:underline font-semibold shrink-0 ml-2">Abrir</a>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Sugestões anônimas enviadas pelo consultor + agendamentos */}
          <SharedClinicsPanel shared={caseData.shared_clinics} caseId={caseData.id} onSelected={fetchAll} />
          <AppointmentsPanel appointments={caseData.appointments} />

          {/* Profissionais indicados pelo consultor — extrai attachments do chat */}
          <IndicatedProfessionalsPanel
            messages={messages}
            onAccept={(c) => setDraft(`Gostei de ${c.name}! Quero agendar a consulta com ele(a).`)}
            onDecline={(c) => setDraft(`Pode me sugerir outro(a) profissional? Prefiro ver alternativas ao(à) ${c.name}.`)}
          />

          {/* Fluxo de chegada — família vê status: aguardando registro, aguardando confirmação, ou confirmado. */}
          {(caseData.arrival || ["reserva_confirmada", "concluido"].includes(caseData.status)) && (
            <ArrivalFlow caseObj={caseData} role="familia" onDone={fetchAll} />
          )}
        </div>

        {/* Sidebar - Timeline + PIN + Chat */}
        <div className="space-y-6">
          {caseData.family_pin && (
            <section className="bg-[#0B2B62] text-white rounded-xl p-5" data-testid="family-pin-section">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-70">
                <Key className="w-3.5 h-3.5" /> PIN de acompanhamento
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="font-mono font-bold text-2xl tracking-[0.35em]" data-testid="family-pin-value">
                  {showPin ? caseData.family_pin : "••••••"}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid="pin-toggle"
                    onClick={() => setShowPin((v) => !v)}
                    className="p-1.5 rounded-md hover:bg-white/10"
                    title={showPin ? "Ocultar" : "Mostrar"}
                  >
                    {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid="pin-copy"
                    onClick={() => { navigator.clipboard.writeText(caseData.family_pin); toast.success("PIN copiado."); }}
                    className="p-1.5 rounded-md hover:bg-white/10"
                    title="Copiar"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-[11px] opacity-70 mt-3 leading-relaxed">
                Use este PIN + o protocolo <b className="font-mono">{caseData.protocol}</b> em <Link to="/acompanhar" className="underline">raf.com.br/acompanhar</Link> para consultar o caso sem precisar entrar na conta.
              </p>
              {caseData.whatsapp_pin_link && (
                <a data-testid="whatsapp-pin-link" href={caseData.whatsapp_pin_link} target="_blank" rel="noreferrer"
                  onClick={() => trackWhatsApp("case_pin_share", { protocol: caseData.protocol })}
                  className="mt-3 inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#20B858]">
                  <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
                </a>
              )}
            </section>
          )}

          {consultantCard && (
            <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="consultor-card">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Seu consultor</div>
              <div className="flex items-start gap-3 group relative">
                {consultantCard.avatar_url ? (
                  <img data-testid="consultor-avatar" src={consultantCard.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-slate-100" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-100">
                    <UserCircle2 className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-[#0B2B62]" data-testid="consultor-name">{consultantCard.name}</div>
                  <div className="text-xs text-slate-500">Consultor RAF · disponível para você</div>
                </div>
                {consultantCard.bio && (
                  <div className="hidden group-hover:block absolute left-0 right-0 top-full mt-2 z-10 bg-white border border-slate-200 shadow-lg rounded-lg p-3 text-xs text-slate-700 leading-relaxed" data-testid="consultor-bio-tooltip">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sobre o profissional</div>
                    {consultantCard.bio}
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl text-[#0B2B62]">Progresso RAF Direct</h2>
              <span
                data-testid="ws-live-indicator"
                className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest ${wsLive ? "text-green-600" : "text-slate-400"}`}
                title={wsLive ? "Conexão em tempo real ativa" : "Reconectando…"}
              >
                {wsLive ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {wsLive ? "ao vivo" : "off"}
              </span>
            </div>
            <CanonicalTimeline
              current={caseData.official_status}
              progress={caseData.progress_percent}
            />
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-display font-bold text-xl text-[#0B2B62] mb-4">Chat com consultor</h2>
            {caseData.family_selection && (
              <div className="mb-3 p-3 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50" data-testid="chat-plan-pin">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Plano escolhido · fixado
                    </div>
                    <div className="mt-1 font-display font-bold text-sm text-[#0B2B62] leading-tight">
                      {caseData.family_selection.plan_tier}
                    </div>
                    <div className="text-xs text-slate-700 mt-0.5">
                      Clínica <b>{caseData.family_selection.clinic_code}</b>
                      {caseData.family_selection.clinic_city && <> · {caseData.family_selection.clinic_city}/{caseData.family_selection.clinic_state}</>}
                    </div>
                    {caseData.family_selection.plan_monthly_price != null && (
                      <div className="text-xs font-mono text-emerald-700 font-bold mt-0.5">
                        R$ {Number(caseData.family_selection.plan_monthly_price).toLocaleString("pt-BR",{minimumFractionDigits:2})}/mês
                      </div>
                    )}
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/plan-receipt.pdf`}
                     target="_blank" rel="noopener noreferrer"
                     data-testid="chat-plan-pin-receipt"
                     className="shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-[#0B2B62] text-white rounded-md text-[10px] font-semibold hover:bg-slate-800">
                    <FileDown className="w-3 h-3" /> PDF
                  </a>
                </div>
              </div>
            )}
            <div className="h-64 overflow-y-auto space-y-2 pr-1 mb-3" data-testid="case-chat">
              {messages.length === 0 && <p className="text-sm text-slate-500">Nenhuma mensagem. Envie sua dúvida ou aguarde o consultor.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`msg-in max-w-[85%] ${m.sender_role === "consultor" ? "" : "ml-auto"}`}>
                  <div className={`rounded-lg px-3 py-2 text-sm ${m.sender_role === "consultor" ? "bg-slate-100 text-slate-800" : "bg-[#0B2B62] text-white"}`}>
                    {m.content}
                    {m.attachment?.type === "professional_card" && (
                      <div className="mt-1.5 text-[11px] italic opacity-80">
                        📌 Veja o card completo em <b>"Profissionais indicados pelo consultor"</b> acima.
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{m.sender_name}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                placeholder="Escreva uma mensagem..."
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
              <button data-testid="chat-send" onClick={sendMsg} className="p-2 bg-[#0B2B62] text-white rounded-md hover:bg-slate-800 transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </section>
        </div>
      </div>
      {showReview && (
        <ReviewModal
          caseId={caseId}
          protocol={caseData.protocol}
          patientName={`${caseData.patient_name}, ${caseData.patient_age}`}
          initial={myReview}
          onClose={() => setShowReview(false)}
          onSaved={(r) => { setMyReview(r); setShowReview(false); }}
        />
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);

const ListSection = ({ title, items }) => (
  <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    {(!items || items.length === 0) ? <p className="text-xs text-slate-400">— não informado</p> : (
      <ul className="space-y-1">{items.map((it, i) => <li key={`${it}-${i}`} className="text-sm text-slate-700">• {it}</li>)}</ul>
    )}
  </div>
);

export default CaseView;
